/**
 * In-app notification tone (foreground only).
 *
 * Web Push background notifications get their sound from the OS, which the web
 * app cannot override. But when the app is OPEN (foreground), the service
 * worker posts a `PLAY_NOTIFICATION_SOUND` message and we play a short,
 * clearly-audible chime here using the Web Audio API (no asset file needed).
 *
 * Browsers block audio until the user has interacted with the page, so we
 * lazily create / resume the AudioContext on the first user gesture.
 */

let audioCtx = null;
let unlocked = false;

function getCtx() {
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtx = new AC();
  }
  return audioCtx;
}

/** Resume the audio context (called on a user gesture). */
function unlockAudio() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  unlocked = true;
}

/** Play a single bell-like note. */
function playNote(ctx, freq, startTime, duration) {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startTime);

  // Quick attack, smooth exponential decay → pleasant "ding"
  gain.gain.setValueAtTime(0.0001, startTime);
  gain.gain.exponentialRampToValueAtTime(0.6, startTime + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, startTime + duration);

  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(startTime);
  osc.stop(startTime + duration);
}

/** Play a two-note "ding-dong" notification chime. */
export function playNotificationChime() {
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    // Not yet unlocked by a gesture — try to resume, but don't throw
    ctx.resume().catch(() => {});
  }
  const now = ctx.currentTime;
  // Note 1: E6, Note 2: C6 — a warm, recognisable alert
  playNote(ctx, 1318.51, now, 0.45);
  playNote(ctx, 1046.5, now + 0.18, 0.55);

  // Also nudge a vibration on devices that support it
  if (navigator.vibrate) {
    try { navigator.vibrate([200, 100, 200]); } catch (e) { /* ignore */ }
  }
}

/**
 * Wire up the in-app tone:
 *  - Unlock audio on the first user interaction.
 *  - Listen for the service worker's PLAY_NOTIFICATION_SOUND message.
 * Idempotent — safe to call once on app mount.
 */
export function initNotificationSound() {
  if (typeof window === 'undefined') return;

  const onFirstGesture = () => {
    unlockAudio();
    if (unlocked) {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    }
  };
  window.addEventListener('pointerdown', onFirstGesture, { passive: true });
  window.addEventListener('keydown', onFirstGesture);

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'PLAY_NOTIFICATION_SOUND') {
        playNotificationChime();
      }
    });
  }
}
